import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API, productImage } from "../lib/brand";
import ArtCard from "../components/ArtCard";
import { Disc3, ArrowRight, Music } from "lucide-react";

function albumCover(album) {
  if (album.cover_path) return { url: `${API}/files/${album.cover_path}`, fallback: false };
  // Fallback: use first track's cover if any track has an uploaded cover
  const firstWithCover = (album.tracks || []).find((t) => t.cover_path || t.cover_url);
  if (firstWithCover) return { url: productImage(firstWithCover), fallback: false };
  return { url: `${API}/brand/knot.png`, fallback: true };
}

function AlbumCard({ album }) {
  const { url, fallback } = albumCover(album);
  return (
    <Link
      to={`/album/${album.id}`}
      data-testid={`album-card-${album.id}`}
      className="group relative block bg-[#09120C] border border-[#1A2B20] overflow-hidden"
    >
      <div className="relative aspect-square overflow-hidden bg-black">
        <img
          src={url}
          alt={album.title}
          className={`absolute inset-0 w-full h-full transition-transform duration-[1400ms] group-hover:scale-105 ${
            fallback ? "object-contain p-16 opacity-70" : "object-cover"
          }`}
          draggable="false"
          onContextMenu={(e) => e.preventDefault()}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
        <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 px-2 py-1 bg-black/80">
          <Disc3 className="w-3 h-3 text-[#279153]" />
          <span className="text-[10px] tracking-[0.25em] uppercase text-white">Album</span>
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-5">
          <h3 className="font-serif-display text-2xl text-white leading-tight mb-1">{album.title}</h3>
          <div className="text-xs text-[#95A89C] tracking-wide">
            {album.track_count} track{album.track_count === 1 ? "" : "s"}
            {album.album_price != null && <span> · ${Number(album.album_price).toFixed(2)}</span>}
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function MusicGallery() {
  const [albums, setAlbums] = useState([]);
  const [singles, setSingles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/albums`),
      axios.get(`${API}/products`, { params: { type: "music" } }),
    ])
      .then(([al, pr]) => {
        setAlbums(al.data || []);
        const all = pr.data || [];
        setSingles(all.filter((t) => !t.album_id));
      })
      .finally(() => setLoading(false));
  }, []);

  const isEmpty = !loading && albums.length === 0 && singles.length === 0;

  return (
    <div data-testid="gallery-music" className="pt-32 pb-24 max-w-7xl mx-auto px-6 lg:px-12">
      <div className="border-b border-[#1A2B20] pb-10 mb-12">
        <div className="text-[11px] tracking-[0.4em] uppercase text-[#279153] mb-3">Sounds of the isle</div>
        <h1 className="font-serif-display text-5xl sm:text-6xl text-white mb-4">Music</h1>
        <p className="text-base text-[#95A89C] max-w-2xl">
          Acoustic ballads, drones, and slow airs — recorded in the studio, released directly.
        </p>
      </div>

      {loading ? (
        <div className="text-[#95A89C] text-sm">Loading the collection…</div>
      ) : isEmpty ? (
        <div
          data-testid="coming-soon-placeholder"
          className="relative overflow-hidden border border-[#1A2B20] bg-[#09120C]"
        >
          <img src={`${API}/brand/knot.png`} alt="" aria-hidden="true"
               className="absolute -right-20 -bottom-24 w-96 h-96 object-contain opacity-[0.10] pointer-events-none" />
          <div className="relative px-8 md:px-16 py-20 md:py-28 max-w-2xl">
            <div className="inline-flex items-center gap-3 mb-8">
              <span className="w-8 h-px bg-[#279153]" />
              <Music className="w-4 h-4 text-[#279153]" />
              <span className="text-[10px] tracking-[0.4em] uppercase text-[#279153]">Ag teacht — Coming Soon</span>
            </div>
            <h2 className="font-serif-display text-5xl text-white leading-[1.05] mb-6">
              The studio is<br />
              <em className="not-italic font-medium text-[#279153]">still recording</em>.
            </h2>
            <p className="text-base text-[#95A89C] mb-10">
              New ballads in the works. Subscribe below to be first to hear a release.
            </p>
            <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3 bg-[#1E7040] hover:bg-[#165932] text-white text-xs tracking-[0.3em] uppercase">
              Notify me <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      ) : (
        <div className="space-y-16">
          {albums.length > 0 && (
            <section data-testid="albums-section">
              <div className="text-[11px] tracking-[0.4em] uppercase text-[#5C7064] mb-4">Albums</div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {albums.map((a) => <AlbumCard key={a.id} album={a} />)}
              </div>
            </section>
          )}
          {singles.length > 0 && (
            <section data-testid="singles-section">
              <div className="text-[11px] tracking-[0.4em] uppercase text-[#5C7064] mb-4">Singles</div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {singles.map((p) => <ArtCard key={p.id} product={p} />)}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
